import { ReactNode, useState, useRef } from "react";
import { ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";

interface TableColumn<T> {
  key: keyof T | string;
  header: string;
  width?: string;
  render?: (row: T) => ReactNode;
  description?: string;
}

interface TableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  maxHeight?: string;
  defaultWidth?: string;
  shadow?: boolean;
  fixedColumns?: number;
  noMaxHeight?: boolean;
  compact?: boolean;
  sortBy?: string;
  alternateRowColor?: boolean;
}

function Table<T>({
  data,
  columns,
  maxHeight = "400px",
  defaultWidth = "150px",
  shadow = true,
  fixedColumns = 0,
  noMaxHeight = false,
  compact = false,
  alternateRowColor = true,
  sortBy,
}: TableProps<T>) {
  const [sortColumn, setSortColumn] = useState<keyof T | string | null>(
    sortBy ? sortBy : columns[0].key,
  );

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const tableRef = useRef<HTMLDivElement>(null);

  const handleSortColumn = (column: keyof T | string) => {
    if (column === sortColumn) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = [...data].sort((a, b) => {
    if (sortColumn) {
      const valA = a[sortColumn as keyof T];
      const valB = b[sortColumn as keyof T];

      if (typeof valA === "string" && typeof valB === "string") {
        return sortDirection === "asc"
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      } else if (typeof valA === "number" && typeof valB === "number") {
        return sortDirection === "asc" ? valA - valB : valB - valA;
      } else if (valA instanceof Date && valB instanceof Date) {
        return sortDirection === "asc"
          ? valA.getTime() - valB.getTime()
          : valB.getTime() - valA.getTime();
      }
      return 0;
    }
    return 0;
  });

  return (
    <div
      className={`${shadow ? "shadow-md" : "border"} rounded-lg mt-4 overflow-hidden`}
      style={{
        maxHeight: noMaxHeight ? undefined : maxHeight,
        position: "relative",
      }}
    >
      <div
        ref={tableRef}
        className="overflow-auto"
        style={{
          maxHeight: noMaxHeight ? undefined : maxHeight,
          position: "relative",
        }}
      >
        <table className="w-full text-sm text-left text-gray-500 table-fixed">
          <thead className="bg-neutral-50">
            <tr>
              {columns.map((column, index) => {
                const toolTipclassName = `column-header-tooltip-${column.header.replace(/\s+/g, "")}`;
                const isFixed = index < fixedColumns;

                return (
                  <th
                    key={column.key.toString()}
                    scope="col"
                    className={`px-6 ${compact ? "py-1.5" : "py-3"} bg-neutral-50 cursor-pointer select-none ${isFixed ? "sticky" : ""
                      }`}
                    style={{
                      width: column.width ?? defaultWidth,
                      left: isFixed
                        ? `${columns.slice(0, index).reduce((sum, col) => sum + parseInt(col.width || defaultWidth), 0)}px`
                        : "auto",
                    }}
                    onClick={() => handleSortColumn(column.key)}
                  >
                    <div className="flex items-center">
                      <span className="text-xs text-neutral-700 uppercase">
                        <a className={toolTipclassName}>{column.header}</a>
                        {column.description ? (
                          <Tooltip
                            anchorSelect={`.${toolTipclassName}`}
                            place="top"
                          >
                            {column.description}
                          </Tooltip>
                        ) : null}
                      </span>
                      {sortColumn === column.key && (
                        <span className="ml-2">
                          {sortDirection === "asc" ? (
                            <ArrowUpIcon className="h-4 w-4" />
                          ) : (
                            <ArrowDownIcon className="h-4 w-4" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((row, rowIndex) => {
              const rowClass = alternateRowColor
                ? rowIndex % 2 === 0
                  ? "bg-neutral-100"
                  : "bg-white"
                : "bg-white";

              return (
                <tr
                  key={rowIndex}
                  className={`border-b hover:bg-neutral-50 ${rowClass}`}
                >
                  {columns.map((column, index) => {
                    const isFixed = index < fixedColumns;
                    return (
                      <td
                        key={column.key.toString()}
                        className={`px-6 ${compact ? "py-1.5" : "py-4"} overflow-hidden text-ellipsis ${isFixed ? "sticky" : ""}`}
                        style={{
                          width: column.width ?? defaultWidth,
                          left: isFixed
                            ? `${columns.slice(0, index).reduce((sum, col) => sum + parseInt(col.width || defaultWidth), 0)}px`
                            : "auto",
                          background: isFixed ? "inherit" : "transparent",
                          zIndex: isFixed ? 10 : "auto",
                        }}
                      >
                        <div className="break-words">
                          {column.render
                            ? column.render(row)
                            : (row[column.key as keyof T] as ReactNode)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Table;
